/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import { App, Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import PhoneInput from 'src/components/ts-components/PhoneInput'
import BaseModal from 'src/components/ts-components/BaseModal'
import { CountrySelectInput } from 'src/components/ts-components/CountrySelectInput'
import { standardizePhoneForSubmission } from '@sportninja/common/utils/utils'
import BusinessAccountInfo from '../BusinessAccountInfo'

type UpgradeAccountModalProps = {
  email: string
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  fetchMe: () => void
}

// Business info input fields interface (copied from businessInfo.tsx)
export interface BusinessInfoInputFields {
  title: string
  companyEmail: string
  orgAbbreviation: string
  phone: string
  companyName: string
  street: string
  country: string
  state: string
  city: string
  postcode: string
}

const initialValues = {
  title: '',
  companyEmail: '',
  phone: '',
  companyName: '',
  street: '',
  country: '',
  state: '',
  city: '',
  postcode: '',
  orgAbbreviation: '',
}

// Helper function to create abbreviation from company name (copied from businessInfo.tsx)
function createAbbreviation(companyName: string) {
  let text = companyName.replace(/\s/g, '') // Remove all whitespaces
  // clear any special characters or emojis
  text = text.replace(/[^a-zA-Z0-9]/g, '')
  // Check if input is valid
  if (!text) {
    return '-' // Return an empty string if the input is empty or invalid
  }
  if (typeof text !== 'string') {
    return '-' // Return an empty string if the input is not a string
  }
  if (text.length < 5) {
    return text.toUpperCase() // Return the input in uppercase if it has less than 5 characters
  }
  // Get the first 5 characters of the text
  const abbreviation = text.slice(0, 5)

  // Ensure the abbreviation has at least one character and return it in uppercase
  return abbreviation.length > 0 ? abbreviation.toUpperCase() : ''
}

/**
 * Calls the sign-up validation endpoint for upgrade flow.
 * @returns server success payload or throws the raw error object
 */
const validateUpgradeForm = async (data: any) => {
  try {
    return await req('/account/upgrade/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (err) {
    console.error('🔴 validateUpgradeForm:', err)
    throw err
  }
}

/**
 * Calls the account upgrade endpoint to submit the upgrade request.
 * @returns server success payload or throws the raw error object
 */
const submitUpgradeForm = async (data: any) => {
  try {
    return await req('/account/upgrade', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (err) {
    console.error('🔴 submitUpgradeForm:', err)
    throw err
  }
}

const setCustomErrors = (
  invalid_fields: Record<string, string>,
  setCustomError: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const add = (field: keyof BusinessInfoInputFields, msg: string) => {
    setCustomError((prev) => ({ ...prev, [field]: msg }))
    setFieldErrors((prev) => [...new Set([...prev, field])])
  }

  if (invalid_fields['account.title']) {
    add('title', invalid_fields['account.title'])
  }
  if (invalid_fields['account.email']) {
    add('companyEmail', invalid_fields['account.email'])
  }
  if (invalid_fields['account.phone_number']) {
    add('phone', invalid_fields['account.phone_number'])
  }
  if (invalid_fields['account.company_name']) {
    add('companyName', invalid_fields['account.company_name'])
  }
  if (invalid_fields['account.org_abbreviation']) {
    add('orgAbbreviation', invalid_fields['account.org_abbreviation'])
  }
  if (invalid_fields['account.address_street']) {
    add('street', invalid_fields['account.address_street'])
  }
  if (invalid_fields['account.address_country']) {
    add('country', invalid_fields['account.address_country'])
  }
  if (invalid_fields['account.address_state_province']) {
    add('state', invalid_fields['account.address_state_province'])
  }
  if (invalid_fields['account.address_city']) {
    add('city', invalid_fields['account.address_city'])
  }
  if (invalid_fields['account.address_postal_code']) {
    add('postcode', invalid_fields['account.address_postal_code'])
  }
}

const UpgradeAccountModal = ({
  email,
  isOpen,
  onClose,
  onComplete,
  fetchMe,
}: UpgradeAccountModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()
  const [isLoading, setIsLoading] = useState(false)

  // Form state management
  const { handleSubmit, setValue, watch, getValues } =
    useForm<BusinessInfoInputFields>({
      defaultValues: {
        ...initialValues,
        companyEmail: email,
      },
    })

  useEffect(() => {
    if (isOpen) {
      fetchMe()
      setValue('companyEmail', email)
    }
  }, [isOpen, fetchMe, email])

  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] =
    useState<Record<string, string>>(initialValues)
  const [generalError, setGeneralError] = useState<string>('')

  // Phone input state (separate from form since PhoneInput has different interface)
  const [phoneValue, setPhoneValue] = useState('')

  // Auto-abbreviation effect
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'companyName' && value.companyName) {
        setValue('orgAbbreviation', createAbbreviation(value.companyName))
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue])

  // Submit handler - validates form and submits upgrade request
  const handleUpgrade = useCallback(async () => {
    try {
      setIsLoading(true)

      // Clear previous errors
      setFieldErrors([])
      setCustomError(initialValues)
      setGeneralError('')

      // Get form values
      const formData = getValues()

      // Standardize phone number for submission
      const standardizedPhone = standardizePhoneForSubmission(phoneValue)

      // Build validation payload (mirrors BusinessInfo structure)
      const validationPayload = {
        account: {
          title: formData.title,
          email: formData.companyEmail,
          phone_number: standardizedPhone,
          company_name: formData.companyName,
          org_abbreviation: formData.orgAbbreviation,
          address_street: formData.street,
          address_country: formData.country,
          address_state_province: formData.state,
          address_city: formData.city,
          address_postal_code: formData.postcode,
        },
      }

      console.log('🟢 handleUpgrade: Validation payload:', validationPayload)

      // Validate form using API
      await validateUpgradeForm(validationPayload)

      // If validation passes, proceed with actual upgrade submission
      const upgradePayload = {
        account: {
          phone_number: standardizedPhone,
          company_name: formData.companyName,
          org_abbreviation: formData.orgAbbreviation,
          address_street: formData.street,
          address_country: formData.country,
          address_state_province: formData.state,
          address_city: formData.city,
          address_postal_code: formData.postcode,
        },
      }

      console.log(
        '🟢 handleUpgrade: Submitting upgrade request:',
        upgradePayload
      )

      // Submit the upgrade request
      await submitUpgradeForm(upgradePayload)

      // Success - show message and close modal
      message.success('Account Upgraded Successfully!')
      onComplete()
      onClose()
    } catch (error: any) {
      console.error('🔴 handleUpgrade: Error during validation:', error)

      if (error?.invalid_fields) {
        // Handle validation errors by setting field-specific errors
        setCustomErrors(error.invalid_fields, setCustomError, setFieldErrors)
      } else {
        // Handle other errors with generic message
        setGeneralError('Failed to validate upgrade request')
      }
    } finally {
      setIsLoading(false)
    }
  }, [getValues, phoneValue, message, onClose, onComplete])

  const handleCancel = useCallback(() => {
    // Reset form state
    setValue('title', '')
    setValue('companyEmail', '')
    setValue('companyName', '')
    setValue('orgAbbreviation', '')
    setValue('street', '')
    setValue('city', '')
    setValue('state', '')
    setValue('postcode', '')
    setValue('country', '')
    setPhoneValue('')
    setFieldErrors([])
    setCustomError(initialValues)
    setGeneralError('')
    onClose()
  }, [setValue, onClose])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button text='Cancel' onClick={handleCancel} variant='secondary' />
        <Button
          text={isLoading ? 'Submitting...' : 'Upgrade Account'}
          onClick={handleUpgrade}
          variant='primary'
          disabled={isLoading}
        />
      </div>
    )
  }, [isMobile, handleCancel, isLoading, handleUpgrade])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title='Upgrade to Business Account'
      actions={getFooter()}
      desktopWidth='60vw'
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(handleUpgrade)()
        }}
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {/* Business Account Info Section */}
        <BusinessAccountInfo />

        {/* Contact Info Section */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <Text
            variant='captionLarge'
            weight='semiBold'
            color={colors.NEUTRAL_0}
          >
            CONTACT INFO
          </Text>

          {/* Company Name and Title Row */}
          <div
            css={css`
              display: flex;
              gap: 16px;
              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='companyName'
                value={watch('companyName') || ''}
                setValue={(value) => setValue('companyName', value)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Organization Name *'
                autoComplete='organization'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='title'
                value={watch('title') || ''}
                setValue={(value) => setValue('title', value)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Title'
                autoComplete='organization-title'
              />
            </div>
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
              ${isMobile &&
              css`
                width: 100%;
              `}
            `}
          >
            {/* Abbreviation (read-only) */}
            <TextInput
              name='orgAbbreviation'
              value={watch('orgAbbreviation') || ''}
              setValue={() => {}} // Read-only
              fieldErrors={fieldErrors}
              customError={customError}
              label='Abbreviation (Auto-generated)'
              disabled={true}
            />
          </div>

          {/* Company Email and Phone Row */}
          <div
            css={css`
              display: flex;
              gap: 16px;
              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='companyEmail'
                value={watch('companyEmail') || ''}
                setValue={() => {}} // Read-only
                fieldErrors={fieldErrors}
                customError={customError}
                label='Company Email *'
                type='email'
                autoComplete='username'
                disabled={true}
              />
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <PhoneInput
                value={phoneValue}
                onChange={setPhoneValue}
                label='Phone Number *'
                error={
                  fieldErrors.includes('phone') ? customError.phone : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding-top: 16px;
            border-top: 1px solid #3f4753;
          `}
        >
          <Text
            variant='captionLarge'
            weight='semiBold'
            color={colors.NEUTRAL_0}
          >
            ADDRESS
          </Text>

          {/* Street Address */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              ${isMobile &&
              css`
                width: 100%;
              `}
            `}
          >
            <TextInput
              name='street'
              value={watch('street') || ''}
              setValue={(value) => setValue('street', value)}
              fieldErrors={fieldErrors}
              customError={customError}
              label='Street *'
              autoComplete='street-address'
            />
          </div>

          {/* City and Postal Code Row */}
          <div
            css={css`
              display: flex;
              gap: 16px;
              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='city'
                value={watch('city') || ''}
                setValue={(value) => setValue('city', value)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='City *'
                autoComplete='address-level2'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='postcode'
                value={watch('postcode') || ''}
                setValue={(value) => setValue('postcode', value)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Postal Code *'
                autoComplete='postal-code'
              />
            </div>
          </div>

          {/* Country and State Row */}
          <div
            css={css`
              display: flex;
              gap: 16px;
              @media (max-width: 768px) {
                flex-direction: column;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <CountrySelectInput
                name='country'
                value={watch('country') || undefined}
                onChange={(value) => setValue('country', value || '')}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Country *'
              />
            </div>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                flex: 1 0 0;
                ${isMobile &&
                css`
                  width: 100%;
                `}
              `}
            >
              <TextInput
                name='state'
                value={watch('state') || ''}
                setValue={(value) => setValue('state', value)}
                fieldErrors={fieldErrors}
                customError={customError}
                label='Province/State *'
                autoComplete='address-level1'
              />
            </div>
          </div>
        </div>

        {/* General Error Alert */}
        {generalError && (
          <div
            css={css`
              margin-top: 16px;
            `}
          >
            <Alert
              message={generalError}
              type='error'
              showIcon
              closable
              onClose={() => setGeneralError('')}
            />
          </div>
        )}
      </form>
    </BaseModal>
  )
}

export default UpgradeAccountModal
